// MIDAS V3 — User message bubble (Phase 2)
//
// Right-aligned bubble per spec §3 + handoff chat-window.jsx (lines 736-745):
//   • maxWidth 440
//   • padding 9px 14px
//   • radius "12px 12px 4px 12px"
//   • bg var(--user-bub), border var(--user-bub-line)
//   • text Sora 14, color var(--ink)
//
// Renders any `ChatMessage` whose `role === "user"`. Preserves whitespace via
// `whiteSpace: "pre-wrap"` so Shift+Enter newlines survive.
//
// Phase 2 keeps user-uploaded image rendering minimal — Phase 2 does not yet
// re-implement the v2 bot-proxy authenticated image fetch (that lives in
// `ChatUserMessageV2`). Blob: URLs (in-session uploads) render directly;
// proxy paths render as a small text fallback so the user sees that an image
// was attached. Full image rendering ports in a follow-up phase.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useState } from "react";

import { ChatMessage } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";
// R5 sub-task 6: clickable user-uploaded image — dispatch openArtifact
// (mirror of v2 MidasChatAreaV2.handleImageClick). Lifting state to the
// caller would be cleaner separation but the bubble is the ONLY consumer,
// so dispatching directly here keeps the change minimal and self-contained.
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { openArtifact } from "../../midasPageSlice";

export interface ChatUserMessageV3Props {
  message: ChatMessage;
  /** Show a small timestamp below the bubble (last in group). */
  showTimestamp?: boolean;
}

const formatTime = (ts: string) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const isBlobUrl = (url: string): boolean => url.startsWith("blob:");

const ChatUserMessageV3: React.FC<ChatUserMessageV3Props> = ({
  message,
  showTimestamp = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();

  // Normalise the v1 legacy "[image]" sentinel that older clients sent.
  const raw = message.text ?? "";
  const displayText = raw === "[image]" ? "" : raw;
  const hasText = displayText.trim().length > 0;
  const hasImage = !!(message.imageUrl && message.imageUrl.length > 0);
  const imageIsBlob = hasImage && isBlobUrl(message.imageUrl!);

  // R5 sub-task 6: open the user-uploaded image in the v3 artifact dock on
  // click. Restricted to blob: URLs (in-session uploads) — the bot-proxy
  // path (saved history threads) is not yet rendered in v3 per the file's
  // own docstring, so click-to-open there is N/A. openArtifact dedupes by
  // url+page so repeated clicks on the same image just re-activate the
  // existing tab.
  const [imgHovered, setImgHovered] = useState(false);
  const handleImageClick = () => {
    if (!hasImage || !imageIsBlob) return;
    const defaultTitle = translate("midas.v3.userImage.defaultTitle", {
      defaultValue: "Image",
    }) as string;
    dispatch(
      openArtifact({
        type: "image",
        title: message.imageName || defaultTitle,
        source: "machineid",
        url: message.imageUrl!,
      })
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        width: "100%",
        marginBottom: 22,
      }}
    >
      <div
        style={{
          maxWidth: 440,
          padding: "9px 14px",
          borderRadius: "12px 12px 4px 12px",
          background: "var(--user-bub)",
          border: "1px solid var(--user-bub-line)",
          color: "var(--ink)",
          fontSize: 14,
          lineHeight: 1.45,
          fontFamily: "var(--font-sans)",
          letterSpacing: "-0.005em",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          // sendFailed → red left-bar accent without the MUI alert chrome.
          borderLeftWidth: message.sendFailed ? 3 : 1,
          borderLeftColor: message.sendFailed ? "var(--red)" : "var(--user-bub-line)",
        }}
        aria-label={
          message.sendFailed
            ? (translate("sendError") as string)
            : undefined
        }
      >
        {hasImage && imageIsBlob && (
          <div
            role="button"
            tabIndex={0}
            onClick={handleImageClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleImageClick();
              }
            }}
            onMouseEnter={() => setImgHovered(true)}
            onMouseLeave={() => setImgHovered(false)}
            aria-label={
              (translate("midas.v3.userImage.openInDock", {
                defaultValue: "Open image in artifact pane",
              }) as string)
            }
            title={
              (translate("midas.v3.userImage.openInDock", {
                defaultValue: "Open image in artifact pane",
              }) as string)
            }
            style={{
              marginBottom: hasText ? 8 : 0,
              maxWidth: 240,
              borderRadius: 8,
              overflow: "hidden",
              cursor: "pointer",
              transition: "opacity 120ms ease",
              opacity: imgHovered ? 0.85 : 1,
              outline: "none",
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
            <img
              src={message.imageUrl!}
              alt={message.imageName || "image"}
              loading="lazy"
              decoding="async"
              style={{
                width: "100%",
                maxHeight: 200,
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        )}
        {hasImage && !imageIsBlob && !hasText && (
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: "var(--muted)",
            }}
          >
            {message.imageName || "image"}
          </span>
        )}
        {hasText && <span>{displayText}</span>}
      </div>
      {showTimestamp && (
        <span
          className="mono"
          style={{
            marginTop: 4,
            marginRight: 4,
            fontSize: 10,
            color: "var(--faint)",
            letterSpacing: "0.04em",
          }}
        >
          {formatTime(message.timestamp)}
        </span>
      )}
    </div>
  );
};

export default ChatUserMessageV3;
